import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, Plus, Trash2, Edit, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CatalogoImpuestos = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ nombre: '', ambito: 'Nacional', periodicidad_default: 'Mensual', requiere_municipio: false });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/catalogo-impuestos`);
      setItems(data);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditForm({ ...item });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/catalogo-impuestos/${editingId}`, editForm);
      setEditingId(null);
      load();
    } catch { alert('Error al guardar'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de impuesto del catálogo?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/catalogo-impuestos/${id}`);
      load();
    } catch { alert('Error al eliminar'); }
  };

  const addItem = async () => {
    if (!addForm.nombre.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/catalogo-impuestos`, addForm);
      setShowAdd(false);
      setAddForm({ nombre: '', ambito: 'Nacional', periodicidad_default: 'Mensual', requiere_municipio: false });
      load();
    } catch { alert('Error al crear'); }
  };

  if (loading) return <p className="text-sm text-slate-500">Cargando catálogo...</p>;

  return (
    <div data-testid="catalogo-impuestos">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#1F3864]" />
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Catálogo Maestro de Impuestos</h2>
        </div>
        <button onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#1F3864] hover:bg-[#172b4d] text-white rounded-md text-xs font-medium"
          data-testid="catalogo-add-btn">
          <Plus className="w-3.5 h-3.5" />Agregar
        </button>
      </div>

      {showAdd && (
        <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input type="text" placeholder="Nombre del impuesto" value={addForm.nombre}
              onChange={(e) => setAddForm(p => ({ ...p, nombre: e.target.value }))}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="catalogo-add-nombre" />
            <select value={addForm.ambito} onChange={(e) => setAddForm(p => ({ ...p, ambito: e.target.value }))}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm">
              <option value="Nacional">Nacional</option>
              <option value="Municipal">Municipal</option>
            </select>
            <select value={addForm.periodicidad_default} onChange={(e) => setAddForm(p => ({ ...p, periodicidad_default: e.target.value }))}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm">
              <option value="Mensual">Mensual</option>
              <option value="Bimestral">Bimestral</option>
              <option value="Trimestral">Trimestral</option>
              <option value="Cuatrimestral">Cuatrimestral</option>
              <option value="Anual">Anual</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={addForm.requiere_municipio}
                onChange={(e) => setAddForm(p => ({ ...p, requiere_municipio: e.target.checked }))}
                className="rounded" />
              Requiere municipio
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={addItem} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium">Guardar</button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md text-xs font-medium">Cancelar</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b">
            <tr>
              <th className="py-2 px-3 text-left">Nombre</th>
              <th className="py-2 px-3 text-left">Ámbito</th>
              <th className="py-2 px-3 text-left">Periodicidad</th>
              <th className="py-2 px-3 text-center">Municipio</th>
              <th className="py-2 px-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item._id} className="border-b border-slate-100 hover:bg-slate-50">
                {editingId === item._id ? (
                  <>
                    <td className="py-2 px-3">
                      <input type="text" value={editForm.nombre} onChange={(e) => setEditForm(p => ({ ...p, nombre: e.target.value }))}
                        className="border border-slate-300 rounded px-2 py-1 text-sm w-full" />
                    </td>
                    <td className="py-2 px-3">
                      <select value={editForm.ambito} onChange={(e) => setEditForm(p => ({ ...p, ambito: e.target.value }))}
                        className="border border-slate-300 rounded px-2 py-1 text-sm">
                        <option value="Nacional">Nacional</option>
                        <option value="Municipal">Municipal</option>
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      <select value={editForm.periodicidad_default} onChange={(e) => setEditForm(p => ({ ...p, periodicidad_default: e.target.value }))}
                        className="border border-slate-300 rounded px-2 py-1 text-sm">
                        <option value="Mensual">Mensual</option>
                        <option value="Bimestral">Bimestral</option>
                        <option value="Trimestral">Trimestral</option>
                        <option value="Cuatrimestral">Cuatrimestral</option>
                        <option value="Anual">Anual</option>
                      </select>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <input type="checkbox" checked={editForm.requiere_municipio || false}
                        onChange={(e) => setEditForm(p => ({ ...p, requiere_municipio: e.target.checked }))} className="rounded" />
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={saveEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2 px-3 font-medium text-slate-800">{item.nombre}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.ambito === 'Nacional' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>
                        {item.ambito}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-600">{item.periodicidad_default}</td>
                    <td className="py-2 px-3 text-center">{item.requiere_municipio ? '✓' : '—'}</td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(item)} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`catalogo-edit-${item._id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteItem(item._id)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`catalogo-delete-${item._id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CatalogoImpuestos;
